import { TrendingUp, Building2, Users2, Briefcase, Crown, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const TIERS = [
  {
    icon: Users2,
    title: "Advisor",
    commission: "1.50%",
    perks: ["Customer Lead Generation", "Loan Product Guidance", "Part-Time / Full-Time"],
    tone: "from-[#1B3F7A]/40",
  },
  {
    icon: TrendingUp,
    title: "DSA",
    commission: "Up to 2.00%",
    perks: ["Direct Selling Agent", "Customer Acquisition", "Loan Sourcing + Incentives"],
    tone: "from-[#1B3F7A]/60",
  },
  {
    icon: Building2,
    title: "Agency Partner",
    commission: "2.50% – 5.00%",
    perks: ["Fixed Salary + Commission", "Team Override Income", "Leadership Bonus"],
    tone: "from-[#D4AF37]/22",
    highlight: true,
  },
  {
    icon: Briefcase,
    title: "Business Partner",
    commission: "Profit Sharing",
    perks: ["Profit Sharing + Incentives", "Company Branches", "Build Local Presence"],
    tone: "from-[#D4AF37]/35",
  },
];

const INCOME_CATEGORIES = [
  "Direct Commission", "Override Commission", "Team Commission", "Performance Bonus",
  "Leadership Bonus", "Referral Bonus", "Branch Incentive", "Franchise Incentive",
  "Profit Sharing", "Annual Recognition Awards",
];

export default function CareerLadder() {
  return (
    <section
      id="career-ladder"
      data-testid="career-ladder"
      className="relative bg-[#0A1F44] text-white py-24 md:py-32 overflow-hidden noise-overlay"
    >
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full border border-[#D4AF37]/8" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center">
            <span className="gold-divider text-[11px] tracking-[0.32em] uppercase">
              · Institutional structure ·
            </span>
            <h2
              data-testid="ladder-heading"
              className="font-display text-[40px] md:text-[60px] mt-5 leading-[1.05] tracking-tight"
            >
              The Sahyog <em className="not-italic text-[#D4AF37]">career ladder</em>
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl mx-auto">
              Start as an Advisor &amp; grow into a Business Partner — four clear tiers with
              compounding income at every step.
            </p>
          </div>
        </Reveal>

        {/* Tier flow */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t, i) => (
            <Reveal key={t.title} delay={i * 90}>
              <div
                data-testid={`tier-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative h-full rounded-2xl p-6 border ${
                  t.highlight ? "border-[#D4AF37]/70" : "border-[#D4AF37]/25"
                } bg-gradient-to-b ${t.tone} to-transparent hover:-translate-y-1 transition-all duration-300 overflow-hidden`}
              >
                {/* Tier number */}
                <div className="absolute top-4 right-4 font-display text-white/15 text-3xl">
                  0{i + 1}
                </div>
                {/* Promotion arrow (desktop only between cards) */}
                {i < TIERS.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 z-10 text-[#D4AF37]">
                    <ChevronRight size={28} strokeWidth={2.5} />
                  </div>
                )}

                <div className="tile-icon">
                  <t.icon size={22} strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-display text-2xl uppercase tracking-wider">
                  {t.title}
                </h3>
                <div className="mt-2 inline-flex items-center gap-1.5 text-[#F0D788]">
                  <Crown size={13} />
                  <span className="font-display text-xl">{t.commission}</span>
                </div>

                <ul className="mt-4 space-y-2">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-white/70">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        {/* 10 Income categories chip cloud */}
        <Reveal delay={250}>
          <div className="mt-16 rounded-3xl border border-[#D4AF37]/25 bg-white/[0.03] p-6 md:p-8">
            <div className="text-center">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                Multiple income categories
              </span>
              <h3 className="font-display text-2xl md:text-3xl mt-2">
                Earn through <em className="not-italic text-[#F0D788]">10 different streams</em>
              </h3>
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-2.5">
              {INCOME_CATEGORIES.map((cat) => (
                <span
                  key={cat}
                  data-testid={`income-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[12px] text-[#F0D788] hover:border-[#D4AF37]/70 transition"
                >
                  <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* CTA + utility strip */}
        <Reveal delay={350}>
          <div
            data-testid="ladder-utility-strip"
            className="mt-12 grid grid-cols-1 min-[400px]:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 text-center"
          >
            {[
              "No Fixed Income Required",
              "18 – 60 yrs Eligibility",
              "Easy Documentation",
              "Fast Payout",
              "Training & Guidance",
              "Digital Tools + CRM",
            ].map((u) => (
              <div
                key={u}
                className="rounded-xl py-3 px-2 border border-[#D4AF37]/20 bg-white/[0.03] text-[11px] uppercase tracking-wide text-white/80 break-words"
              >
                {u}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={420}>
          <div className="mt-10 flex justify-center">
            <a
              data-testid="ladder-cta"
              href="/apply/advisor"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold text-sm font-semibold"
            >
              Join a Trusted Brand · Earn Without Limits
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
